import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, Users, TrendingUp, MousePointer, Copy, Check, 
  Download, ExternalLink, Calendar, Award, CreditCard, BarChart 
} from 'lucide-react';
import api from '../api/api';
import { useToast } from '../components/ui/Toast';
import { log } from '../utils/productionLogger';

const AffiliateDashboardPage = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [copiedLink, setCopiedLink] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchReferrals();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/affiliates/dashboard');
      setData(response.data);
    } catch (error) {
      showToast('Error loading dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await api.get('/affiliates/referrals');
      log.debug('📊 Referrals API Response:', response.data);
      
      // Handle both response formats: { data: { referrals, stats } } or { referrals, stats }
      const responseData = response.data.data || response.data;
      
      log.debug('📋 Referrals array:', responseData.referrals);
      log.debug('📈 Referral stats:', responseData.stats);
      
      setReferrals(responseData.referrals || []);
      setReferralStats(responseData.stats || {});
    } catch (error) {
      log.error('❌ Error loading referrals:', error);
      log.error('Error details:', error.response?.data);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedLink(type);
    setTimeout(() => setCopiedLink(''), 2000);
  };

  const handleRequestPayout = async () => {
    if (!stats || (stats?.pendingEarnings || 0) < 1000) {
      showToast('Minimum withdrawal amount is ₹1,000', 'warning');
      return;
    }

    setPayoutLoading(true);
    try {
      const response = await api.post('/affiliates/payouts', { 
        amount: stats?.pendingEarnings || 0
      });
      
      if (response.data.success) {
        showToast('Payout request submitted successfully!', 'success');
        // Refresh dashboard data to update pending earnings
        await fetchDashboardData();
      } else {
        showToast(response.data.error || 'Failed to request payout', 'error');
      }
    } catch (error) {
      showToast('Error requesting payout: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleCopyTemplate = (template) => {
    navigator.clipboard.writeText(template)
      .then(() => {
        showToast('Template copied to clipboard!', 'success');
      })
      .catch(() => {
        showToast('Failed to copy template', 'error');
      });
  };

  const handleDownloadResource = async (resourceType) => {
    setDownloadLoading(true);
    try {
      const response = await api.get('/affiliates/materials');
      
      if (response.data.success) {
        const materials = response.data.materials;
        let downloadUrl = '';
        
        if (resourceType === 'banners') {
          downloadUrl = materials.banners?.downloadUrl || materials.banners?.[0];
        } else if (resourceType === 'social') {
          downloadUrl = materials.socialMedia?.downloadUrl || materials.socialMedia?.[0];
        }
        
        if (downloadUrl) {
          // Create temporary link and trigger download
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = resourceType === 'banners' ? 'banner-pack.zip' : 'social-media-kit.zip';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          showToast('Download started!', 'success');
        } else {
          showToast('Resource not available at this time', 'warning');
        }
      } else {
        showToast('Failed to fetch resources', 'error');
      }
    } catch (error) {
      showToast('Error downloading resource: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setDownloadLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold text-black mb-4">Affiliate Not Found</h2>
          <p className="text-gray-600 mb-6">Please apply to become an affiliate first.</p>
          <Link 
            to="/affiliate/signup" 
            className="inline-block px-6 py-3 bg-yellow-400 text-black font-bold rounded-md hover:bg-yellow-500"
          >
            Apply Now
          </Link>
        </div>
      </div>
    );
  }

  // Handle nested data structure (backend wraps in { success: true, data: {...} })
  const { affiliate, stats, recentReferrals, recentPayouts } = data.data || data;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black mb-2">Affiliate Dashboard</h1>
          <p className="text-gray-600">Welcome back, {affiliate?.name || 'Affiliate'}!</p>
          {affiliate?.status === 'pending' && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-yellow-800 font-semibold">⏳ Your application is under review</p>
              <p className="text-yellow-700 text-sm mt-1">We'll notify you once it's approved.</p>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <DollarSign size={32} className="text-green-500" />
              <span className="text-xs text-gray-500">Total</span>
            </div>
            <h3 className="text-2xl font-bold text-black mb-1">
              ₹{(stats?.totalEarnings || 0).toLocaleString()}
            </h3>
            <p className="text-gray-600 text-sm">Total Earnings</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <CreditCard size={32} className="text-yellow-500" />
              <span className="text-xs text-gray-500">Available</span>
            </div>
            <h3 className="text-2xl font-bold text-black mb-1">
              ₹{(stats?.pendingEarnings || 0).toLocaleString()}
            </h3>
            <p className="text-gray-600 text-sm">Pending Balance</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <Users size={32} className="text-blue-500" />
              <span className="text-xs text-green-600">
                +{stats?.thisMonthReferrals || 0} this month
              </span>
            </div>
            <h3 className="text-2xl font-bold text-black mb-1">
              {stats?.totalReferrals || 0}
            </h3>
            <p className="text-gray-600 text-sm">Total Referrals</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp size={32} className="text-purple-500" />
              <span className="text-xs text-gray-500">CVR</span>
            </div>
            <h3 className="text-2xl font-bold text-black mb-1">
              {(stats?.conversionRate || 0).toFixed(1)}%
            </h3>
            <p className="text-gray-600 text-sm">Conversion Rate</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {['overview', 'referrals', 'payouts', 'materials'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 font-semibold capitalize ${
                    activeTab === tab
                      ? 'text-yellow-600 border-b-2 border-yellow-600'
                      : 'text-gray-600 hover:text-black'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Performance This Month */}
                <div>
                  <h3 className="text-lg font-bold text-black mb-4">This Month's Performance</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-600 text-sm mb-1">Earnings</p>
                      <p className="text-2xl font-bold text-black">
                        ₹{(stats?.thisMonthEarnings || 0).toLocaleString()}
                      </p>
                      {(stats?.lastMonthEarnings || 0) > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {(((stats?.thisMonthEarnings || 0) - (stats?.lastMonthEarnings || 0)) / (stats?.lastMonthEarnings || 1) * 100).toFixed(1)}% vs last month
                        </p>
                      )}
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-600 text-sm mb-1">Referrals</p>
                      <p className="text-2xl font-bold text-black">{stats?.thisMonthReferrals || 0}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-600 text-sm mb-1">Clicks</p>
                      <p className="text-2xl font-bold text-black">{stats?.totalClicks || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Active Referrals (Recurring Commissions) */}
                <div>
                  <h3 className="text-lg font-bold text-black mb-2">Active Employers (Recurring 20%)</h3>
                  <p className="text-sm text-gray-600 mb-4">You earn 20% commission monthly while they stay subscribed</p>
                  <div className="space-y-3">
                    {recentReferrals.length > 0 ? (
                      recentReferrals.filter(r => r.status === 'active').map((referral) => (
                        <div key={referral.id} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-semibold text-black">{referral.employerName}</p>
                              <p className="text-xs text-gray-600">{referral.employerEmail}</p>
                            </div>
                            <div className="text-right">
                              <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                {(referral.currentPlan || 'FREE').toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-green-200">
                            <div>
                              <p className="text-xs text-gray-600">Plan Amount</p>
                              <p className="font-bold text-black">₹{(referral.planAmount || 0).toLocaleString()}/mo</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Your Commission</p>
                              <p className="font-bold text-green-600">₹{(referral.monthlyCommission || 0).toLocaleString()}/mo</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Total Earned</p>
                              <p className="font-bold text-black">₹{(referral.totalCommissionEarned || 0).toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                            <Calendar size={12} />
                            <span>Joined: {new Date(referral.signupDate).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Users size={48} className="mx-auto mb-2 opacity-50" />
                        <p>No active employers yet. Share your link to get started!</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Referrals Tab */}
            {activeTab === 'referrals' && (
              <div>
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-black mb-2">Your Referred Employers</h3>
                  {referralStats && (
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-600 mb-1">Total Referrals</p>
                        <p className="text-2xl font-bold text-black">{referralStats.totalReferrals || 0}</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-600 mb-1">Active (Paying)</p>
                        <p className="text-2xl font-bold text-green-600">{referralStats.activeReferrals || 0}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-600 mb-1">Inactive</p>
                        <p className="text-2xl font-bold text-gray-600">{referralStats.inactiveReferrals || 0}</p>
                      </div>
                      <div className="bg-yellow-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-600 mb-1">Monthly Earnings</p>
                        <p className="text-2xl font-bold text-yellow-600">₹{(referralStats.totalMonthlyCommission || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {referrals.length > 0 ? (
                    referrals.map((referral) => (
                      <div key={referral.employerId} className={`p-4 rounded-lg border ${
                        referral.isActive 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-bold text-black">{referral.companyName}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                referral.isActive
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {(referral.subscriptionStatus || 'inactive').toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{referral.employerName} • {referral.employerEmail}</p>
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <Calendar size={12} />
                              Joined: {new Date(referral.joinedDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-sm font-bold rounded">
                              {(referral.subscriptionPlan || 'free').toUpperCase()}
                            </span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-3 pt-3 border-t">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Plan Amount</p>
                            <p className="font-bold text-black">₹{(referral.subscriptionAmount || 0).toLocaleString()}/mo</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Your Commission (20%)</p>
                            <p className="font-bold text-green-600">₹{(referral.monthlyCommission || 0).toLocaleString()}/mo</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Total Earned</p>
                            <p className="font-bold text-black">₹{(referral.totalCommissionEarned || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Status</p>
                            <p className={`text-sm font-semibold ${referral.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                              {referral.isActive ? '✓ Earning' : '○ Not Active'}
                            </p>
                          </div>
                        </div>

                        {/* Subscription Dates */}
                        {referral.subscriptionStartDate && (
                          <div className="mt-3 pt-3 border-t flex items-center gap-4 text-xs text-gray-600">
                            <div>
                              <span className="font-semibold">Start:</span> {new Date(referral.subscriptionStartDate).toLocaleDateString()}
                            </div>
                            {referral.subscriptionEndDate && (
                              <div>
                                <span className="font-semibold">End:</span> {new Date(referral.subscriptionEndDate).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {!referral.isActive && referral.subscriptionStatus === 'inactive' && (
                          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                            ⏸️ This employer is on free plan. You'll earn commission when they upgrade to Pro plan.
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Users size={48} className="mx-auto mb-3 opacity-50" />
                      <p className="font-semibold">No referrals yet</p>
                      <p className="text-sm mt-1">Share your referral code to start earning commissions!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payouts Tab */}
            {activeTab === 'payouts' && (
              <div className="space-y-6">
                {/* Request Payout */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-lg border border-green-200">
                  <h3 className="font-bold text-black mb-2">Available for Withdrawal</h3>
                  <p className="text-3xl font-bold text-green-600 mb-4">
                    ₹{(stats?.pendingEarnings || 0).toLocaleString()}
                  </p>
                  <button
                    disabled={(stats?.pendingEarnings || 0) < 1000 || payoutLoading}
                    onClick={handleRequestPayout}
                    className="px-6 py-3 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {payoutLoading ? 'Processing...' : 'Request Payout'}
                  </button>
                  <p className="text-xs text-gray-600 mt-2">Minimum withdrawal: ₹1,000</p>
                </div>

                {/* Payout History */}
                <div>
                  <h3 className="text-lg font-bold text-black mb-4">Payout History</h3>
                  <div className="space-y-3">
                    {recentPayouts.length > 0 ? (
                      recentPayouts.map((payout) => (
                        <div key={payout.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-semibold text-black">₹{(payout.amount || 0).toLocaleString()}</p>
                            <p className="text-sm text-gray-600">
                              {new Date(payout.requestedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                            payout.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : payout.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {payout.status}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <CreditCard size={48} className="mx-auto mb-2 opacity-50" />
                        <p>No payouts yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Marketing Materials Tab */}
            {activeTab === 'materials' && (
              <div className="space-y-6">
                {/* Referral Links */}
                <div>
                  <h3 className="text-lg font-bold text-black mb-4">Referral Links</h3>
                  <div className="space-y-3">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Homepage</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={`https://jobloop.in?ref=${affiliate.referralCode}`}
                          readOnly
                          className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
                        />
                        <button
                          onClick={() => copyToClipboard(`https://jobloop.in?ref=${affiliate.referralCode}`, 'home')}
                          className="px-4 py-2 bg-yellow-400 text-black font-semibold rounded-md hover:bg-yellow-500"
                        >
                          {copiedLink === 'home' ? <Check size={18} /> : <Copy size={18} />}
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Employer Signup</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={`https://jobloop.in/auth/employer?ref=${affiliate.referralCode}`}
                          readOnly
                          className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
                        />
                        <button
                          onClick={() => copyToClipboard(`https://jobloop.in/auth/employer?ref=${affiliate.referralCode}`, 'employer')}
                          className="px-4 py-2 bg-yellow-400 text-black font-semibold rounded-md hover:bg-yellow-500"
                        >
                          {copiedLink === 'employer' ? <Check size={18} /> : <Copy size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media Templates */}
                <div>
                  <h3 className="text-lg font-bold text-black mb-4">Social Media Templates</h3>
                  <div className="space-y-3">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="font-semibold text-black mb-2">Twitter/X</p>
                      <p className="text-sm text-gray-700 mb-3">
                        Join JobLoop and hire top talent! Sign up with my referral code {affiliate.referralCode} 🚀
                      </p>
                      <button 
                        onClick={() => handleCopyTemplate(`Join JobLoop and hire top talent! Sign up with my referral code ${affiliate.referralCode} 🚀`)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                      >
                        Copy Template
                      </button>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="font-semibold text-black mb-2">LinkedIn</p>
                      <p className="text-sm text-gray-700 mb-3">
                        I'm partnering with JobLoop, an amazing recruitment platform. Use my code {affiliate.referralCode} when signing up! 💼
                      </p>
                      <button 
                        onClick={() => handleCopyTemplate(`I'm partnering with JobLoop, an amazing recruitment platform. Use my code ${affiliate.referralCode} when signing up! 💼`)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                      >
                        Copy Template
                      </button>
                    </div>
                  </div>
                </div>

                {/* Download Resources */}
                <div>
                  <h3 className="text-lg font-bold text-black mb-4">Download Resources</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <button 
                      onClick={() => handleDownloadResource('banners')}
                      disabled={downloadLoading}
                      className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download size={24} className="text-yellow-500 mb-2" />
                      <p className="font-semibold text-black">Banner Pack</p>
                      <p className="text-sm text-gray-600">{downloadLoading ? 'Downloading...' : 'Various sizes for websites'}</p>
                    </button>
                    <button 
                      onClick={() => handleDownloadResource('social')}
                      disabled={downloadLoading}
                      className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download size={24} className="text-yellow-500 mb-2" />
                      <p className="font-semibold text-black">Social Media Kit</p>
                      <p className="text-sm text-gray-600">{downloadLoading ? 'Downloading...' : 'Images for all platforms'}</p>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateDashboardPage;
